'use client';

import React, { useState } from 'react';
import { toast } from 'sonner';
import {
  CheckCircle, AlertTriangle, XCircle, RefreshCw, Settings,
  ExternalLink, Webhook, Database, Mail, MessageSquare, Clock,
} from 'lucide-react';

interface Integration {
  id: string;
  name: string;
  category: string;
  icon: React.ElementType;
  status: 'connected' | 'degraded' | 'disconnected';
  lastSync: string;
  syncInterval: string;
  endpoint: string;
  details: string;
  ticketsCreated?: number;
  eventsForwarded?: number;
}

const integrations: Integration[] = [
  {
    id: 'int-snow', name: 'ServiceNow ITSM', category: 'Ticketing',
    icon: Database, status: 'connected', lastSync: '09:24:51', syncInterval: '30s',
    endpoint: 'https://axiomnet.service-now.com/api/now/v2',
    details: 'Auto-creates P1/P2 tickets · Bi-directional status sync · SLA escalation',
    ticketsCreated: 1847,
  },
  {
    id: 'int-slack', name: 'Slack Notifications', category: 'Alerting',
    icon: MessageSquare, status: 'connected', lastSync: '09:24:44', syncInterval: 'Real-time',
    endpoint: 'hooks.slack.com/services/T0…',
    details: '#netops-alerts · #p1-incidents · @oncall mentions',
    eventsForwarded: 4218,
  },
  {
    id: 'int-pagerduty', name: 'PagerDuty Oncall', category: 'Alerting',
    icon: AlertTriangle, status: 'degraded', lastSync: '09:18:02', syncInterval: '60s',
    endpoint: 'events.pagerduty.com/v2/enqueue',
    details: 'API key rotation required — escalation policies may be delayed',
    eventsForwarded: 312,
  },
  {
    id: 'int-webhook-1', name: 'Custom Webhook — NOC Portal', category: 'Webhook',
    icon: Webhook, status: 'connected', lastSync: '09:24:50', syncInterval: 'Real-time',
    endpoint: 'https://noc.axiomnet.internal/hooks/netopsai',
    details: 'Incident payload · Remediation status · Node telemetry snapshot',
    eventsForwarded: 9341,
  },
  {
    id: 'int-email', name: 'Email Escalation', category: 'Notification',
    icon: Mail, status: 'connected', lastSync: '09:21:00', syncInterval: 'On-trigger',
    endpoint: 'smtp.axiomnet.com:587',
    details: 'P1 escalation to noc-manager@axiomnet.com · 15min SLA breach warning',
  },
  {
    id: 'int-syslog', name: 'Syslog Forwarder', category: 'Telemetry',
    icon: Database, status: 'disconnected', lastSync: 'Never',  syncInterval: 'Streaming',
    endpoint: 'syslog-collector-03.axiomnet.internal:514',
    details: 'Connection refused — verify collector service status',
  },
];

const statusConfig = {
  connected: { icon: CheckCircle, color: 'text-success', bg: 'bg-success/10', border: 'border-success/20', label: 'Connected' },
  degraded: { icon: AlertTriangle, color: 'text-warning', bg: 'bg-warning/10', border: 'border-warning/20', label: 'Degraded' },
  disconnected: { icon: XCircle, color: 'text-danger', bg: 'bg-danger/10', border: 'border-danger/20', label: 'Disconnected' },
};

export default function IntegrationStatusSection() {
  const [syncing, setSyncing] = useState<string | null>(null);

  const handleSync = (id: string, name: string) => {
    setSyncing(id);
    setTimeout(() => {
      setSyncing(null);
      toast.success(`${name} sync completed`);
    }, 1500);
  };

  return (
    <div className="bg-surface border border-border rounded-xl p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Webhook size={15} className="text-primary" />
          <p className="text-sm font-semibold text-foreground">Integrations</p>
          <span className="text-[10px] bg-surface-raised border border-border px-2 py-0.5 rounded-full text-muted font-mono">
            {integrations.length} configured
          </span>
        </div>
        <button
          onClick={() => toast.info('Opening integration marketplace…')}
          className="text-[11px] text-primary hover:text-primary/80 transition-colors"
        >
          + Add Integration
        </button>
      </div>

      <div className="space-y-2.5 max-h-[520px] overflow-y-auto scrollbar-hide">
        {integrations.map((intg) => {
          const cfg = statusConfig[intg.status];
          const StatusIcon = cfg.icon;
          const IntgIcon = intg.icon;
          const isSyncing = syncing === intg.id;

          return (
            <div
              key={intg.id}
              className={`border rounded-xl p-3.5 transition-all duration-150 hover:border-opacity-60
                ${intg.status === 'disconnected' ? 'border-danger/20 bg-danger/5' :
                  intg.status === 'degraded'? 'border-warning/20 bg-warning/5' : 'border-border hover:bg-surface-raised/50'}`}
            >
              <div className="flex items-start gap-3">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${cfg.bg}`}>
                  <IntgIcon size={15} className={cfg.color} />
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <p className="text-[12px] font-semibold text-foreground">{intg.name}</p>
                    <span className={`text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded border ${cfg.bg} ${cfg.color} ${cfg.border}`}>
                      {cfg.label}
                    </span>
                    <span className="text-[9px] bg-surface-overlay text-muted px-1.5 py-0.5 rounded border border-border uppercase tracking-wide font-semibold">
                      {intg.category}
                    </span>
                  </div>

                  <p className="text-[11px] text-muted truncate font-mono">{intg.endpoint}</p>
                  <p className="text-[11px] text-foreground-muted mt-0.5 leading-snug">{intg.details}</p>

                  <div className="flex items-center gap-3 mt-2 text-[10px] text-muted">
                    <div className="flex items-center gap-1">
                      <Clock size={9} />
                      <span>Sync: {intg.syncInterval}</span>
                    </div>
                    <span>·</span>
                    <span>Last: {intg.lastSync}</span>
                    {intg.ticketsCreated !== undefined && (
                      <>
                        <span>·</span>
                        <span className="text-primary font-semibold">{intg.ticketsCreated.toLocaleString()} tickets</span>
                      </>
                    )}
                    {intg.eventsForwarded !== undefined && (
                      <>
                        <span>·</span>
                        <span className="text-primary font-semibold">{intg.eventsForwarded.toLocaleString()} events</span>
                      </>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    onClick={() => handleSync(intg.id, intg.name)}
                    className="p-1.5 rounded hover:bg-surface-overlay text-muted hover:text-foreground transition-colors"
                    title="Force sync"
                    disabled={isSyncing}
                  >
                    <RefreshCw size={12} className={isSyncing ? 'animate-spin text-primary' : ''} />
                  </button>
                  <button
                    onClick={() => toast.info(`Opening settings for ${intg.name}…`)}
                    className="p-1.5 rounded hover:bg-surface-overlay text-muted hover:text-foreground transition-colors"
                    title="Configure"
                  >
                    <Settings size={12} />
                  </button>
                  <button
                    onClick={() => toast.info(`Opening ${intg.name} portal…`)}
                    className="p-1.5 rounded hover:bg-surface-overlay text-muted hover:text-primary transition-colors"
                    title="Open portal"
                  >
                    <ExternalLink size={12} />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}